import { useEffect, useState } from 'react';
import { ChevronRight, ClipboardCheck, Clock3, Play } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { loadFreeTests, loadFreeVideos, loadMiscCategories, loadRecentVideos } from '../lib/library';
import type { FreeTest, FreeVideo, MiscCategory, RecentVideo } from '../lib/library';
import { ImageWithFallback, SectionHeading } from './ui';

/**
 * Legacy home dashboard rows rebuilt on live data (Phase 3): recent videos
 * from watch history, free videos/tests, and miscellaneous collections.
 * Each row hides itself while empty so the dashboard never shows stubs.
 */
export function HomeContentRows() {
  const navigate = useNavigate();
  return <>
    <RecentVideosRow onSeeMore={() => navigate('/bookmarks')} />
    <FreeVideosRow onSeeMore={() => navigate('/free-videos')} />
    <FreeTestsRow onSeeMore={() => navigate('/free-tests')} />
    <MiscRow onSeeMore={() => navigate('/library')} />
  </>;
}

function RecentVideosRow({ onSeeMore }: RowProps) {
  const [videos, setVideos] = useState<RecentVideo[]>();
  useEffect(() => { let active = true; void loadRecentVideos(10).then((rows) => { if (active) setVideos(rows); }).catch(() => { if (active) setVideos([]); }); return () => { active = false; }; }, []);
  if (!videos?.length) return null;
  return <section className="course-section">
    <SectionHeading title="Recent videos" action={onSeeMore} actionLabel="See more" />
    <div className="hscroll-row">
      {videos.map((entry) => <Link className="video-card" key={entry.id} to={`/video/${entry.id}`}>
        <span className="video-card-art"><ImageWithFallback src={entry.video?.VideoLogo} className="video-card-image" fallback={<Play size={18} fill="currentColor" />} /><i style={{ width: `${progressPercent(entry)}%` }} /></span>
        <strong>{entry.video?.VideoName ?? 'Lecture'}</strong>
        <small><Clock3 size={11} /> Paused at {formatClock(entry.time)}</small>
      </Link>)}
    </div>
  </section>;
}

function FreeVideosRow({ onSeeMore }: RowProps) {
  const [videos, setVideos] = useState<FreeVideo[]>();
  useEffect(() => { let active = true; void loadFreeVideos(10).then((rows) => { if (active) setVideos(rows); }).catch(() => { if (active) setVideos([]); }); return () => { active = false; }; }, []);
  if (!videos?.length) return null;
  return <section className="course-section">
    <SectionHeading title="Free videos" action={onSeeMore} actionLabel="See more" />
    <div className="hscroll-row">
      {videos.map((video) => <Link className="video-card" key={video.objectId} to={`/video/${video.objectId}`}>
        <span className="video-card-art"><ImageWithFallback src={video.VideoLogo} className="video-card-image" fallback={<Play size={18} fill="currentColor" />} /></span>
        <em>Free demo lecture</em>
        <strong>{video.VideoName}</strong>
      </Link>)}
    </div>
  </section>;
}

function FreeTestsRow({ onSeeMore }: RowProps) {
  const [tests, setTests] = useState<FreeTest[]>();
  useEffect(() => { let active = true; void loadFreeTests(5).then((rows) => { if (active) setTests(rows); }).catch(() => { if (active) setTests([]); }); return () => { active = false; }; }, []);
  if (!tests?.length) return null;
  return <section className="course-section">
    <SectionHeading title="Free tests" action={onSeeMore} actionLabel="See more" />
    <div className="subject-list">{tests.map((test) => <Link className="subject-row" key={test.objectId} to={`/tests/${test.objectId}`}>
      <span className="material-icon"><ClipboardCheck size={16} /></span>
      <div><strong>{test.TestTitle}</strong><small>{Number(test.NoOfQuestions ?? 0) || '\u2014'} questions · {Number(test.Time ?? 60)} min</small></div>
      <ChevronRight size={15} />
    </Link>)}</div>
  </section>;
}

const MISC_TONES = ['tone-a', 'tone-b', 'tone-c', 'tone-d'];

function MiscRow({ onSeeMore }: RowProps) {
  const [categories, setCategories] = useState<MiscCategory[]>();
  useEffect(() => { let active = true; void loadMiscCategories().then((rows) => { if (active) setCategories(rows.slice(0, 8)); }).catch(() => { if (active) setCategories([]); }); return () => { active = false; }; }, []);
  if (!categories?.length) return null;
  return <section className="course-section">
    <SectionHeading title="Miscellaneous" action={onSeeMore} actionLabel="See more" />
    <div className="hscroll-row">
      {categories.map((category, index) => <Link className={`misc-chip ${MISC_TONES[index % MISC_TONES.length]}`} key={category.objectId} to={`/library/${category.objectId}`}>
        <small>{category.CatDesc || 'AyurGuide collection'}</small>
        <strong>{category.CatName}</strong>
      </Link>)}
    </div>
  </section>;
}

type RowProps = { onSeeMore: () => void };

function progressPercent(entry: RecentVideo) {
  if (!entry.totalTime) return 0;
  return Math.min(100, Math.round((entry.time / entry.totalTime) * 100));
}

function formatClock(seconds?: number) {
  const total = Math.max(0, Math.floor(seconds ?? 0));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
